import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { altTextSourceDataAtom, byUrlDataAtom } from './atoms'
import { GeneratedAsset } from './types'
import { Table } from 'shared/ui/Table/Table'
import React, { useMemo } from 'react'
import { Box, Button } from '@mui/material'
import { ImgPreview } from './columns'
import { useSortableColumns } from '../../resources/resourceList/tableUtility'
import { AddButtonTitle, SourceAddition } from './SourceAddition'
import { InfoDisplay } from '../../utility/Containers'

export function SourceTable() {
  const data = useAtomValue(altTextSourceDataAtom)
  const c = useTableColumns()
  const items = useMemo(() => {
    return Object.values(data)
  }, [data])
  const { sorted, columns } = useSortableColumns(Object.values(items), c)
  return (
    <Box>
      <Info />
      <SourceAddition />
      <Table columns={columns} items={sorted} />
    </Box>
  )
}

function useTableColumns() {
  const sourceSetter = useSetAtom(altTextSourceDataAtom)
  const urlData = useAtomValue(byUrlDataAtom)
  const doRemove = (url: string) => {
    sourceSetter((prev) => {
      return Object.entries(prev).reduce((acc, [key, val]) => {
        if (key === url) return acc
        return {
          ...acc,
          [key]: val,
        }
      }, {})
    })
  }

  return useMemo(() => {
    return [
      {
        title: 'URL',
        width: 150,
        rowContent({ url }: GeneratedAsset) {
          return `${url.substring(0, 20)}...${url.substring(url.length - 20)}`
        },
        sortable: 'url',
      },
      {
        title: 'Image',
        width: 50,
        rowContent({ url, altText }: GeneratedAsset) {
          return <ImgPreview src={url} altText={altText} />
        },
      },
      {
        title: 'Alt Text',
        width: 150,
        rowContent({ altText }: GeneratedAsset) {
          return altText
        },
        sortable: 'altText',
      },
      {
        title: 'In Tree',
        width: 50,
        rowContent({ url }: GeneratedAsset) {
          if (url in urlData) {
            return 'Yes'
          }
          return 'No'
        },
        sortable: 'inTree',
        customSort: (a1: GeneratedAsset, a2: GeneratedAsset) => {
          const v1 = a1.url in urlData ? 1 : 0
          const v2 = a2.url in urlData ? 1 : 0
          return v1 - v2
        },
      },
      {
        title: 'Remove',
        width: 50,
        rowContent({ url }: GeneratedAsset) {
          const onclick = () => doRemove(url)
          return <Button onClick={onclick}>Remove</Button>
        },
      },
    ]
  }, [urlData])
}

function Info() {
  return (
    <InfoDisplay>
      <Box>
        This component is where you add source alt text, the data that is used
        to add alt text to the tree.
      </Box>
      <Box>
        In the table below you will see all source alt text. The In Tree column
        tells you if the url is found in the tree.
      </Box>
      <Box>
        You can add alt text either manually or from a Google spreadsheet, click
        {AddButtonTitle} to get started. The columns needed in the spreadsheet
        are [Image URL] and [Alt Text]
      </Box>
    </InfoDisplay>
  )
}
